import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, DotLargeIcon } from '@bi-digital/icons';
import { Button, Dialog, DialogCloseButton, DialogContent, Spinner } from '@bi-digital/react';
import useSWRImmutable from 'swr/immutable';
import type { FetcherError } from '@bino/utils/fetcher';
import { fetcher } from '@bino/utils/fetcher';
import type {
    Course as CourseType,
    CourseDescriptionResponse,
    ElectiveItem,
    Opportunities,
    SemesterItem,
    Opportunity,
} from '../../types';
import { Course } from '../course/Course';
import { OpportunityPromoCard } from '../opportunity-promo-card/OpportunityPromoCard';
import styles from './item-drawer.module.css';
import { TextElement } from '@bino/utils/components';

const electiveToCourse = (elective: ElectiveItem): CourseType => ({
    type: 'course',
    id: elective.id,
    variant: 'Course',
    title: elective.title,
    courseCode: elective.courseCode,
    credits: elective.credits,
});

type DrawerHeaderProps = {
    children: React.ReactNode;
    onClose: () => void;
};

const DrawerHeader = ({ children, onClose }: DrawerHeaderProps) => {
    const { t } = useTranslation();
    return (
        <div className={styles.header} data-color-scheme='dark'>
            <DialogCloseButton aria-label={t('courseDescription.close')} onClick={onClose} />
            <div className={styles.headerContent}>{children}</div>
        </div>
    );
};

type CourseDescriptionViewProps = {
    course: CourseType;
    onClose: () => void;
    onBack?: () => void;
    isCourse?: boolean;
};

const CourseDescriptionView = ({ course, onClose, onBack, isCourse }: CourseDescriptionViewProps) => {
    const { t } = useTranslation();
    const { data, isLoading, error } = useSWRImmutable<CourseDescriptionResponse, FetcherError>(
        course.courseCode
            ? `/api/course-description?subjectCode=${encodeURIComponent(course.courseCode.subjectCode)}&courseNumber=${encodeURIComponent(course.courseCode.courseNumber)}&language=${encodeURIComponent(document.documentElement.lang || 'no')}`
            : null,
        fetcher.get<CourseDescriptionResponse>,
        { keepPreviousData: true },
    );

    return (
        <Dialog open anchor='right' onClose={onClose} className={styles.drawer} data-color-scheme='light'>
            <DrawerHeader onClose={onClose}>
                {onBack && (
                    <Button
                        onClick={onBack}
                        variant='text'
                        skewed='start'
                        size='small'
                        startIcon={<ChevronLeftIcon aria-hidden='true' />}
                    >
                        {t('courseDescription.back')}
                    </Button>
                )}
                <p>
                    {t(
                        course.courseCode
                            ? 'courseDescription.label'
                            : course.variant === 'Elective'
                              ? 'legend.electives'
                              : course.variant === 'Webinar'
                                ? 'legend.webinars'
                                : isCourse
                                  ? 'legend.modules'
                                  : 'legend.courses',
                    )}
                    {course.courseCode && data?.credits !== null && !isLoading && !error && (
                        <>
                            <DotLargeIcon aria-hidden='true' />
                            <span>{t('credits', { count: data?.credits })}</span>
                        </>
                    )}
                </p>
                <h2>{course.title}</h2>
            </DrawerHeader>
            <DialogContent noBorder className={styles.content}>
                {course.description && !course.courseCode ? (
                    <TextElement className={styles.description} rawHtml={course.description} />
                ) : isLoading ? (
                    <Spinner className={styles.spinner} size='large' aria-label={t('courseDescription.loading')} />
                ) : error ? (
                    <>
                        <h3>{t('courseDescription.error')}</h3>
                        <TextElement>
                            <p>{t('courseDescription.errorText')}</p>
                        </TextElement>
                    </>
                ) : data !== undefined ? (
                    <>
                        {data.introduction && (
                            <>
                                <h3>{t('courseDescription.introduction')}</h3>
                                <TextElement rawHtml={data.introduction} />
                            </>
                        )}
                        {data.courseContent && (
                            <>
                                <h3>{t('courseDescription.courseContent')}</h3>
                                <TextElement rawHtml={data.courseContent} />
                            </>
                        )}
                        <h3>{t('courseDescription.disclaimer')}</h3>
                        <TextElement>
                            <p>{t('courseDescription.disclaimerText')}</p>
                        </TextElement>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

type OpportunitiesViewProps = {
    item: Opportunities;
    onClose: () => void;
};

const OpportunitiesView = ({ item, onClose }: OpportunitiesViewProps) => {
    const { t } = useTranslation();
    const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
    const [detailCourse, setDetailCourse] = useState<CourseType | null>(null);

    return (
        <Dialog
            open
            data-color-scheme='dark'
            data-color='primary'
            anchor='right'
            onClose={onClose}
            className={styles.drawer}
        >
            <DrawerHeader onClose={onClose}>
                {selectedOpportunity ? (
                    <>
                        <Button
                            onClick={() => setSelectedOpportunity(null)}
                            variant='text'
                            skewed='start'
                            size='small'
                            startIcon={<ChevronLeftIcon aria-hidden='true' />}
                        >
                            {t('courseDescription.back')}
                        </Button>
                        <h2>{selectedOpportunity.title}</h2>
                    </>
                ) : (
                    <>
                        <p>{t('courseDescription.opportunities.title')}</p>
                        <h2>{item.title}</h2>
                    </>
                )}
            </DrawerHeader>
            {detailCourse && (
                <CourseDescriptionView course={detailCourse} onClose={onClose} onBack={() => setDetailCourse(null)} />
            )}
            {selectedOpportunity ? (
                <DialogContent noBorder className={styles.opportunitiesContent}>
                    {selectedOpportunity.electives && selectedOpportunity.electives.length > 0 ? (
                        <ul className={styles.electives}>
                            {selectedOpportunity.electives.map((elective) => (
                                <Course
                                    key={elective.id}
                                    course={electiveToCourse(elective)}
                                    onOpenItem={() => setDetailCourse(electiveToCourse(elective))}
                                />
                            ))}
                        </ul>
                    ) : (
                        <p>{t('courseDescription.opportunities.electives.empty')}</p>
                    )}
                </DialogContent>
            ) : (
                <DialogContent noBorder className={styles.opportunitiesContent}>
                    <ul className={styles.cards} data-promo-card-list-variant='quadrat-1'>
                        {item.cards.map((card, index) => (
                            <OpportunityPromoCard
                                key={index}
                                opportunity={card}
                                onSelect={() => setSelectedOpportunity(card)}
                            />
                        ))}
                        {item.cards.length === 0 && <p>{t('courseDescription.opportunities.empty')}</p>}
                    </ul>
                </DialogContent>
            )}
        </Dialog>
    );
};

export type ItemDrawerProps = {
    item: SemesterItem | null;
    onClose: () => void;
    isCourse?: boolean;
};

export const ItemDrawer = ({ item, onClose, isCourse }: ItemDrawerProps) => {
    if (item?.type === 'course') {
        return <CourseDescriptionView course={item} onClose={onClose} isCourse={isCourse} />;
    }

    if (item?.type === 'opportunities') {
        return <OpportunitiesView item={item} onClose={onClose} />;
    }

    return null;
};
